// Composer 미리보기 실데이터 컨텍스트 픽커 — 조직▸현장▸점검 + 계정 캐스케이드.
//
// 양식 편집/미리보기에서 reportId 단일 입력 대신 엔티티를 직접 골라 그 실데이터로
// 캔버스가 렌더되게 한다. 상위 엔티티 선택이 바뀌면 하위는 초기화하고 lazy fetch한다.
//   - 조직(companyId): /admin/companies (전역 목록). 변경 시 site/inspection/user 초기화.
//   - 현장(siteId): /admin/sites?organizationId=companyId. 조직 미선택 시 disabled.
//   - 점검(inspectionId): /admin/inspections?organizationId=companyId&siteId. 현장 미선택 시 disabled.
//   - 계정(userId): /admin/users?organizationId=companyId. 조직 미선택 시 disabled.
// 외부 라이브러리 없이 CatalogCombobox(검색형) 재사용 + useEffect lazy fetch.
//
// 결정 기록: docs/decisions/2026-06-22-composer-preview-context-entity-cascade.md

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type {
  PreviewSimulationAxis,
  PreviewSimulationOption,
  ReportComposerPreviewContext,
} from '@safetyhub/shared';
import { adminApi, adminCompaniesApi } from '../../lib/api';
import { Tooltip } from '../../components/Tooltip';
import { CatalogCombobox, type ComboboxOption } from './CatalogCombobox';
import {
  isPlanSourcedAxis,
  parseSimulationAxisInput,
  setSimulationAxisValue,
} from './previewSimulationAxes';
import {
  previewContextReportLabel,
  type EditorPreviewContext,
} from './previewLinkedReport';
import { SimulationAxisRow, resolveSelectIndex } from './SimulationAxisRow';

// 축 행 편집기(위젯 렌더)는 SimulationAxisRow로 추출(2026-09-03) — 전역 속성 패널의 프리셋
// 편집기와 공유. resolveSelectIndex도 그쪽이 정본이고, 기존 소비처(스펙)를 위해 재수출한다.
export { resolveSelectIndex };

/** 요약 칩 표시용 선택 엔티티 라벨(조직/현장/점검/계정). ID→이름 매핑을 잃지 않도록 함께 보관. */
export interface PreviewContextLabels {
  company?: string;
  site?: string;
  inspection?: string;
  user?: string;
  /**
   * 연결한 보고서의 라벨(제목+차수+일자) — 재오픈 시 목록 fetch 완료 전에도 이름을 잃지 않게
   * + 요약 칩 표시용(엔티티 라벨과 같은 문제).
   */
  report?: string;
  /**
   * 모사 축 → 고른 선택지의 라벨(축 key → 라벨). 엔티티 라벨과 같은 문제를 푼다:
   * `opaque` 값(분할 그룹)은 값만 봐서는 이름을 알 수 없고, 옵션은 부수효과 때문에 다시 불러올
   * 수 없다(§④). 그래서 고른 순간의 라벨을 여기 보관해 재오픈·요약 칩에서 그대로 쓴다.
   */
  simulation?: Record<string, string>;
}

interface Props {
  value: EditorPreviewContext;
  onChange: (ctx: EditorPreviewContext) => void;
  disabled?: boolean;
  /**
   * 「보고서(선택)」 단계 노출(결정 2026-09-04 — 명시적 보고서 연결) — 점검(회차) 하위에서
   * 그 점검에 연결된 보고서를 골라 그 저장 데이터로 미리보기한다. 실데이터 반영 모달만 켠다
   * (SimulationPage 등 다른 호스트의 컨텍스트에는 이 축이 의미가 없다).
   */
  showReportPicker?: boolean;
  /** true면 각 항목을 세로 스택(label 위 + w-full 콤보)으로. 미지정 시 기존 인라인(가로) 레이아웃. */
  vertical?: boolean;
  /** 선택 엔티티의 현재 라벨을 보고(요약 칩용). value/옵션 로드에 맞춰 갱신. */
  onLabelsResolved?: (labels: PreviewContextLabels) => void;
  /**
   * 모사 축 **후보 목록** — 선언에서 파생된다(`derivePreviewSimulationAxes`, 결정 2026-09-01 §①).
   *
   * ⚠️ 이 컴포넌트는 축이 무엇을 뜻하는지 **모른다.** `label`·`widget`·`options`만 보고 그리므로
   * 분할이든 다회차든 여기엔 이름조차 없다. 새 축이 선언되면 아무것도 안 해도 후보에 나타난다.
   * 빈 배열/미지정이면 모사 섹션 자체가 없다.
   */
  simulationAxes?: PreviewSimulationAxis[];
  /**
   * `'plan'` 출처 축의 선택지 조회 — ⚠️ **부수효과가 있을 수 있다**(분할 계획은 서버가
   * provider classifier를 돌린다: TbmMeta upsert + AI 정제 캐시 생성). 그래서 호출은 **저작자가
   * 그 축을 추가하는 조작에서 1회**뿐이고, 결과는 그 점검 선택이 유지되는 동안 재사용한다(§④).
   * 점검을 고르는 것만으로는 부르지 않는다 — 저작 중 점검만 바꿔도 운영 데이터가 갱신되면 안 된다.
   */
  onLoadAxisOptions?: (
    axis: PreviewSimulationAxis,
    ctx: ReportComposerPreviewContext,
  ) => Promise<PreviewSimulationOption[]>;
  /**
   * `plan` 출처 축을 「＋ 모사 값 추가」 섹션이 아니라 **캐스케이드 칸**(보고서 하위, 계정 위 —
   * §3 개정 순서: 점검 → 보고서 → 분할 그룹 → 계정. 위=데이터 원천, 아래=형상 조정)으로
   * 그린다(결정 2026-09-04 §1 개정 절의 캐스케이드 승격 — 실데이터 반영 모달만 켠다). 조회
   * 게이트는 그대로다: 저작자가 그 칸의 드롭다운을 **여는 조작에서 1회**(§④ — 섹션의 "축을
   * 추가하는 조작"의 캐스케이드 대응물). 비-plan 축은 종전대로 모사 섹션에 남는다(SimulationPage
   * 등 이 prop을 안 켜는 호스트는 아무것도 달라지지 않는다).
   */
  planAxisAsField?: boolean;
  /** 이미 고른 모사 값의 라벨(축 key → 라벨) — 재오픈 시 opaque 값의 이름을 잃지 않게. */
  initialSimulationLabels?: Record<string, string>;
  /** 이미 연결한 보고서의 라벨 — 재오픈 시 목록 fetch 완료 전에도 이름을 잃지 않게. */
  initialReportLabel?: string;
}

/** ISO 날짜를 YYYY-MM-DD로(없으면 '-'). 점검 라벨 식별용(시간대 변환 불필요한 짧은 식별자). */
function shortDate(iso: string | null | undefined): string {
  if (!iso) return '-';
  return iso.slice(0, 10);
}

/**
 * 모사 값 후보 그룹의 표시 순서·문구 — **묶는 기준은 `axis.group`(선언)이고 여기는 문구뿐이다.**
 * 둘 다 "저작 시점엔 없는 값"이지만 출처가 달라 저작자가 구분할 수 있어야 한다(결정 2026-09-01).
 */
const CANDIDATE_GROUPS: {
  group: PreviewSimulationAxis['group'];
  title: string;
  hint: string;
}[] = [
  { group: 'live', title: '실시간 데이터', hint: '보고서 생성 후 쌓이는 값' },
  { group: 'generation', title: '생성 옵션', hint: '보고서 생성 시점에 정해지는 값' },
  // 2026-09-04 자동연결 폐기 — reportScoped entry·결재 서명 단계 축의 그룹.
  { group: 'report', title: '보고서 데이터', hint: '보고서 문서 자체에 속하는 값' },
];

/** 지정 key들을 뺀 얕은 복사 — 모사 축 로컬 캐시(옵션/에러/라벨) 정리용. */
function omitKeys<T>(map: Record<string, T>, keys: Set<string>): Record<string, T> {
  const next: Record<string, T> = {};
  for (const [k, v] of Object.entries(map)) {
    if (!keys.has(k)) next[k] = v;
  }
  return next;
}

// 점검(회차) 선택 모드 — 현재 단일 선택. 복수 점검이 필요해지면 true로 바꾸면 체크박스 멀티셀렉트가
// 복원된다(handleToggleInspection·inspectionIds 경로 보존). 컨텍스트 타입은 둘 다 지원(inspectionId/inspectionIds).
const ALLOW_MULTI_INSPECTION = false;

export function PreviewContextBar({
  value,
  onChange,
  disabled = false,
  vertical = false,
  onLabelsResolved,
  simulationAxes,
  onLoadAxisOptions,
  planAxisAsField = false,
  initialSimulationLabels,
  initialReportLabel,
  showReportPicker = false,
}: Props) {
  const { companyId, siteId, inspectionId, userId, linkedReportId } = value;
  // 점검 다중 선택(Phase C). inspectionIds 우선, 없으면 단일 inspectionId 1건으로 본다.
  //
  // ⚠️ 메모이즈는 취향이 아니다 — 이 배열은 아래 라벨 보고 effect의 의존이라, 매 렌더 새 배열이면
  // 그 effect가 **매 렌더** 돌며 라벨을 JSON.stringify한다(값은 대개 그대로라 전부 헛일이다).
  const selectedInspectionIds = useMemo(
    () => value.inspectionIds ?? (inspectionId ? [inspectionId] : []),
    [value.inspectionIds, inspectionId],
  );

  const [companies, setCompanies] = useState<ComboboxOption[]>([]);
  const [sites, setSites] = useState<ComboboxOption[]>([]);
  const [inspections, setInspections] = useState<ComboboxOption[]>([]);
  const [users, setUsers] = useState<ComboboxOption[]>([]);
  // 점검에 연결된 보고서 목록(「보고서(선택)」 단계) — showReportPicker일 때만 lazy fetch.
  const [reports, setReports] = useState<ComboboxOption[]>([]);
  // 고른 보고서의 라벨 보관 — 재오픈 시 목록 fetch 완료 전에도 이름을 잃지 않게(엔티티 라벨과
  // 같은 문제 — initialReportLabel로 seed).
  const [reportLabel, setReportLabel] = useState<string | undefined>(
    () => initialReportLabel,
  );

  const [loadErr, setLoadErr] = useState<string | null>(null);

  // 점검(회차) 멀티셀렉트 드롭다운 열림 상태 + 바깥 클릭 닫힘용 ref.
  const [inspectionOpen, setInspectionOpen] = useState(false);
  const inspectionRef = useRef<HTMLDivElement>(null);
  // 트리거의 aria-controls ↔ 펼침 묶음 id. 한 화면에 이 바가 둘 이상 뜰 수 있어 useId로 받는다.
  const inspectionPopupId = useId();

  // ── 모사 축(§③-2 추가형) ────────────────────────────────────────────────
  // 저작자가 **추가한** 축만 행으로 쌓인다. 축을 전부 렌더하지 않는 이유는 편의가 아니라
  // 규약 ④다 — 부수효과가 있는 `plan` 조회가 "축을 추가하는 조작"에 구조적으로 묶인다.
  const axes = useMemo(() => simulationAxes ?? [], [simulationAxes]);
  const simulation = value.previewSimulation;
  // 저작자가 추가했지만 아직 값을 안 고른 축(값이 있으면 아래 activeAxes가 알아서 잡는다).
  const [addedAxisKeys, setAddedAxisKeys] = useState<string[]>([]);
  // `plan` 축의 조회된 선택지 — 축 key → 옵션. 부수효과 때문에 **캐시가 성능이 아니라 안전 장치**다.
  const [axisOptions, setAxisOptions] = useState<Record<string, PreviewSimulationOption[]>>({});
  const [axisLoading, setAxisLoading] = useState<Record<string, boolean>>({});
  const [axisError, setAxisError] = useState<Record<string, string>>({});
  // 고른 선택지의 라벨(값만으론 이름을 알 수 없는 opaque 축 때문에 필요 — Props JSDoc 참조).
  const [axisLabels, setAxisLabels] = useState<Record<string, string>>(
    () => initialSimulationLabels ?? {},
  );
  // 후보 추가 드롭다운.
  const [addOpen, setAddOpen] = useState(false);
  const addRef = useRef<HTMLDivElement>(null);

  const inspectionSig = selectedInspectionIds.join(',');
  // 늦게 온 응답 폐기용 — 조회 중 점검이 바뀌면 그 결과는 **다른 점검의 선택지**다.
  // 클로저의 `selectedInspectionIds`로는 판별할 수 없다(요청 시점 값이 그대로 잡혀 항상 같다).
  const inspectionSigRef = useRef(inspectionSig);
  inspectionSigRef.current = inspectionSig;

  // 캐스케이드 승격(결정 2026-09-04 §1 개정 절) — planAxisAsField면 `plan` 축은 상시 캐스케이드
  // 칸으로 가고, 모사 섹션은 나머지 축만 담는다(모달은 plan만 넘기므로 섹션이 통째로 사라진다).
  const cascadePlanAxes = useMemo(
    () => (planAxisAsField ? axes.filter(isPlanSourcedAxis) : []),
    [axes, planAxisAsField],
  );
  const sectionAxes = useMemo(
    () => (planAxisAsField ? axes.filter((axis) => !isPlanSourcedAxis(axis)) : axes),
    [axes, planAxisAsField],
  );

  // 화면에 뜨는 행 = 후보 순서대로, "추가했거나 값이 있는" 축. 값 기준을 함께 보는 이유는
  // 모달이 재마운트돼도(RealDataModal은 열 때마다 새로 뜬다) 이미 고른 모사가 남아야 하기 때문.
  const activeAxes = useMemo(
    () =>
      sectionAxes.filter(
        (axis) =>
          addedAxisKeys.includes(axis.key) || simulation?.[axis.key] !== undefined,
      ),
    [sectionAxes, addedAxisKeys, simulation],
  );
  const candidateAxes = useMemo(
    () => sectionAxes.filter((axis) => !activeAxes.some((a) => a.key === axis.key)),
    [sectionAxes, activeAxes],
  );

  // 점검 선택이 **바뀌면** `plan` 축의 **조회 결과**를 버린다 — 그 선택지 목록은 이전 점검의
  // 것이라 그대로 두면 남의 그룹을 고르게 된다. 다시 채우려면 저작자가 축을 다시 추가해야 한다
  // (⚠️ 여기서 **재조회하지 않는다** — 조회는 부수효과가 있어 그 조작에서만 1회, §④).
  //
  // ⚠️ **값과 라벨은 지우지 않는다**(2026-09-03 개정). 값은 이제 점검을 바꿔도 유지되고(위 핸들러
  // 주석), 라벨을 지우면 조회 결과가 없는 상태에서 선택지 합성이 무너져 select가 "통합(전체)"을
  // 고른 것처럼 보인다 — 실제 적용은 살아 있으니 **화면만 거짓말하는** 상태가 된다.
  //
  // ⚠️ 의존은 `inspectionSig` **하나뿐**이고 최초 렌더는 건너뛴다. 두 함정이 있다:
  //   ① `axes`를 의존에 넣으면 부모가 배열 리터럴을 내려줄 때마다 identity가 바뀌어 매 렌더
  //      행이 지워진다(저작자가 방금 추가한 축이 사라진다).
  //   ② 마운트에서 돌리면 재오픈 시 넘겨받은 라벨(`initialSimulationLabels`)까지 지워져,
  //      이미 고른 분할 그룹의 이름을 잃는다.
  const prevInspectionSigRef = useRef<string | null>(null);
  const axesRef = useRef(axes);
  axesRef.current = axes;
  useEffect(() => {
    if (prevInspectionSigRef.current === null) {
      prevInspectionSigRef.current = inspectionSig;
      return;
    }
    if (prevInspectionSigRef.current === inspectionSig) return;
    prevInspectionSigRef.current = inspectionSig;
    const planKeys = new Set(
      axesRef.current.filter(isPlanSourcedAxis).map((a) => a.key),
    );
    if (planKeys.size === 0) return;
    setAddedAxisKeys((keys) => keys.filter((k) => !planKeys.has(k)));
    setAxisOptions((prev) => omitKeys(prev, planKeys));
    setAxisError((prev) => omitKeys(prev, planKeys));
  }, [inspectionSig]);

  // 후보 추가 드롭다운 — 바깥 클릭 / Esc → 닫기.
  useEffect(() => {
    if (!addOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (addRef.current && !addRef.current.contains(e.target as Node)) {
        setAddOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAddOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [addOpen]);

  // 점검 드롭다운 — 바깥 클릭 / Esc → 닫기.
  useEffect(() => {
    if (!inspectionOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (inspectionRef.current && !inspectionRef.current.contains(e.target as Node)) {
        setInspectionOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setInspectionOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [inspectionOpen]);

  // 조직 목록 — 마운트 시 1회. (전역, 페이지네이션 상위 100건.)
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const res = await adminCompaniesApi.list({ limit: 100 });
        if (!alive) return;
        setLoadErr(null); // 직전 에러 문구 잔존 방지(성공 경로).
        if (res.success && res.data) {
          setCompanies(
            // 업체명만 표시(사업자등록번호 미표시 — 공간 절약, 2026-07-02).
            res.data.items.map((c) => ({
              value: c.id,
              label: c.name,
            })),
          );
        }
      } catch {
        if (alive) setLoadErr('조직 목록을 불러오지 못했습니다.');
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // 현장 목록 — 조직 변경 시 lazy fetch. 조직 미선택이면 비운다.
  useEffect(() => {
    if (!companyId) {
      setSites([]);
      setLoadErr(null); // 조직 미선택으로 되돌아갈 때 직전 에러 문구 잔존 방지.
      return;
    }
    let alive = true;
    void (async () => {
      try {
        const res = await adminApi.getSites({ organizationId: companyId, limit: 200 });
        if (!alive) return;
        setLoadErr(null); // 직전 에러 문구 잔존 방지(성공 경로).
        if (res.success && res.data) {
          setSites(res.data.map((s) => ({ value: s.id, label: s.name })));
        }
      } catch {
        if (alive) setLoadErr('현장 목록을 불러오지 못했습니다.');
      }
    })();
    return () => {
      alive = false;
    };
  }, [companyId]);

  // 점검 목록 — 조직/현장 변경 시 lazy fetch. 조직 미선택이면 비운다(현장 미선택은 조직 전체 허용).
  useEffect(() => {
    if (!companyId) {
      setInspections([]);
      setLoadErr(null); // 조직 미선택으로 되돌아갈 때 직전 에러 문구 잔존 방지.
      return;
    }
    let alive = true;
    void (async () => {
      try {
        const res = await adminApi.getInspections({
          organizationId: companyId,
          siteId: siteId || undefined,
          limit: 200,
        });
        if (!alive) return;
        setLoadErr(null); // 직전 에러 문구 잔존 방지(성공 경로).
        if (res.success && res.data) {
          setInspections(
            // 회차·날짜만 표시(상태/보고서有 미표시 — 공간 절약, 2026-07-02). 현장명은
            // 드롭다운 우측 key로 유지(현장 미선택 시 변별용).
            res.data.map((i) => ({
              value: i.id,
              // N차 · 사진수 · 발견사항수 · 날짜
              label: `${i.sequenceNumber}차 · 사진 ${i._count.photos} · 발견 ${i._count.findings} · ${shortDate(i.date)}`,
              key: i.site?.name,
            })),
          );
        }
      } catch {
        if (alive) setLoadErr('점검 목록을 불러오지 못했습니다.');
      }
    })();
    return () => {
      alive = false;
    };
  }, [companyId, siteId]);

  // 계정 목록 — 조직 변경 시 lazy fetch. 조직 미선택이면 비운다.
  useEffect(() => {
    if (!companyId) {
      setUsers([]);
      setLoadErr(null); // 조직 미선택으로 되돌아갈 때 직전 에러 문구 잔존 방지.
      return;
    }
    let alive = true;
    void (async () => {
      try {
        const res = await adminApi.getUsers({ organizationId: companyId, limit: 200 });
        if (!alive) return;
        setLoadErr(null); // 직전 에러 문구 잔존 방지(성공 경로).
        if (res.success && res.data) {
          setUsers(
            res.data.map((u) => {
              const email = u.auths.find((a) => a.provider === 'EMAIL')?.providerId;
              return {
                value: u.id,
                label: u.member?.name ?? u.originalName ?? '(이름 없음)',
                key: email,
              };
            }),
          );
        }
      } catch {
        if (alive) setLoadErr('계정 목록을 불러오지 못했습니다.');
      }
    })();
    return () => {
      alive = false;
    };
  }, [companyId]);

  // 보고서 목록 — 점검(단일) 선택 변경 시 lazy fetch(showReportPicker일 때만). 점검 미선택이면
  // 비운다. 부수효과 없는 순수 조회라 재오픈·점검 변경마다 다시 불러도 안전하다(plan 축과 다름).
  useEffect(() => {
    if (!showReportPicker) return;
    if (!inspectionId) {
      setReports([]);
      return;
    }
    let alive = true;
    void (async () => {
      try {
        const res = await adminApi.getReportComposerPreviewContextReports(inspectionId);
        if (!alive) return;
        setLoadErr(null); // 직전 에러 문구 잔존 방지(성공 경로).
        if (res.success && res.data) {
          setReports(
            res.data.map((r) => ({
              value: r.id,
              label: previewContextReportLabel(r),
              key: r.status,
            })),
          );
        }
      } catch {
        if (alive) setLoadErr('보고서 목록을 불러오지 못했습니다.');
      }
    })();
    return () => {
      alive = false;
    };
  }, [showReportPicker, inspectionId]);

  // 선택 엔티티의 라벨을 상위로 보고(요약 칩용). 옵션 로드/선택 변경 시 갱신하되, 값이 실제로
  // 바뀔 때만 호출(sig dedupe)해 부모 재setState 스팸을 막는다.
  //
  // ⚠️ 의존에 `value` 객체를 넣지 말 것 — 본문이 읽는 필드(companyId/siteId/userId/점검·모사)는
  // 이미 각각 의존에 있고, `value`는 부모가 새 객체를 만들 때마다 identity가 바뀌어 sig dedupe가
  // 막아 줄 뿐인 헛돌기를 늘린다. dedupe는 **부모 setState를 막는 장치**이지 이 effect의 실행
  // 비용까지 없애 주지는 않는다.
  const labelsSigRef = useRef<string>('');
  useEffect(() => {
    if (!onLabelsResolved) return;
    const company = companyId ? companies.find((c) => c.value === companyId)?.label : undefined;
    const site = siteId ? sites.find((s) => s.value === siteId)?.label : undefined;
    const inspection =
      selectedInspectionIds.length === 0
        ? undefined
        : selectedInspectionIds.length === 1
          ? (inspections.find((i) => i.value === selectedInspectionIds[0])?.label ?? '1건')
          : `${selectedInspectionIds.length}건`;
    const user = userId ? users.find((u) => u.value === userId)?.label : undefined;
    // 연결 보고서 라벨 — 목록에서 되찾고, 목록이 아직이면 고른 순간 보관한 라벨로(엔티티와 동형).
    const report = linkedReportId
      ? (reports.find((r) => r.value === linkedReportId)?.label ?? reportLabel)
      : undefined;
    // 모사 라벨은 **값이 실제로 실린 축만** 보고한다 — 값 없이 라벨만 남으면 요약 칩이
    // "모사 중"이라 거짓말한다(§⑥은 보이는 것과 걸린 것이 같아야 한다는 규약이다).
    const simulationLabels: Record<string, string> = {};
    for (const [key, label] of Object.entries(axisLabels)) {
      if (simulation?.[key] !== undefined) simulationLabels[key] = label;
    }
    const labels: PreviewContextLabels = {
      company,
      site,
      inspection,
      user,
      ...(report !== undefined ? { report } : {}),
      ...(Object.keys(simulationLabels).length > 0
        ? { simulation: simulationLabels }
        : {}),
    };
    const sig = JSON.stringify(labels);
    if (sig === labelsSigRef.current) return;
    labelsSigRef.current = sig;
    onLabelsResolved(labels);
  }, [companyId, siteId, userId, selectedInspectionIds, companies, sites, inspections, users, onLabelsResolved, axisLabels, simulation, linkedReportId, reports, reportLabel]);

  // ⚠️ **엔티티를 바꿔도 모사 값은 그대로 간다**(결정 2026-09-03 §영속의 범위 — 그 전엔 여기서
  // 계획 산출 축을 즉시 비웠다). 다른 점검의 행 id가 남아 400이 날 수는 있지만, 그건 실제로 실패한
  // 뒤에 그 축만 해제하는 쪽(EditorPage `handleUnverifiedSimulationFailure`)이 낫다 — 미리 비우면
  // **멀쩡한 경우까지** 저작자가 고른 갈래가 조용히 풀리고, 그 편이 훨씬 잦다.
  //
  // 조직 변경 → 하위(현장/점검/계정/보고서) 초기화. 새 객체를 만들므로 모사는 **명시적으로** 실어 나른다.
  const handleCompany = (v: string | undefined) => {
    setReportLabel(undefined);
    onChange({
      companyId: v,
      siteId: undefined,
      inspectionId: undefined,
      inspectionIds: undefined,
      userId: undefined,
      previewSimulation: simulation,
    });
  };
  // 현장 변경 → 점검 초기화(계정은 조직 스코프라 유지). 보고서 연결은 점검 하위라 함께 해제.
  const handleSite = (v: string | undefined) => {
    setReportLabel(undefined);
    onChange({
      ...value,
      siteId: v,
      inspectionId: undefined,
      inspectionIds: undefined,
      linkedReportId: undefined,
      splitPreview: undefined,
    });
  };
  // 점검 다중 토글 → inspectionIds 갱신 + 단일 entry 호환 위해 inspectionId=[0]. (복수 선택 복원 시 사용)
  const handleToggleInspection = (id: string) => {
    const next = selectedInspectionIds.includes(id)
      ? selectedInspectionIds.filter((x) => x !== id)
      : [...selectedInspectionIds, id];
    setReportLabel(undefined);
    onChange({
      ...value,
      inspectionIds: next.length ? next : undefined,
      inspectionId: next[0],
      linkedReportId: undefined,
      splitPreview: undefined,
    });
  };
  // 점검 단일 선택(현재 기본) → inspectionId만 설정(inspectionIds 비움) + 드롭다운 닫기.
  // 보고서 연결은 **그 점검의** 보고서라 점검이 바뀌면 함께 해제한다(결정 2026-09-04).
  const handleSelectInspection = (id: string) => {
    setReportLabel(undefined);
    onChange({
      ...value,
      inspectionId: id,
      inspectionIds: undefined,
      linkedReportId: undefined,
      splitPreview: undefined,
    });
    setInspectionOpen(false);
  };
  // 보고서 연결 선택/해제 — 그 저장 데이터로 미리보기(미선택=현행 컨텍스트 미리보기).
  const handleLinkedReport = (v: string | undefined) => {
    setReportLabel(v ? reports.find((r) => r.value === v)?.label : undefined);
    onChange({ ...value, linkedReportId: v });
  };
  // `plan` 축 선택지 조회 실행부(부수효과: TbmMeta upsert·AI 캐시, §④) — 트리거 두 곳(섹션의
  // 「＋ 모사 값 추가」 / 캐스케이드 칸의 드롭다운 열기)이 같은 캐시·폐기 규약을 지나게 한 곳에
  // 둔다. 조작 자체가 조회 트리거라 "열 때만 부른다"를 사람이 지킬 필요가 없다.
  const loadPlanAxisOptions = (axis: PreviewSimulationAxis) => {
    if (!isPlanSourcedAxis(axis)) return;
    if (!onLoadAxisOptions) return;
    if (axisOptions[axis.key]) return; // 이 점검 선택의 결과를 이미 갖고 있다
    if (axisLoading[axis.key]) return;
    setAxisLoading((prev) => ({ ...prev, [axis.key]: true }));
    setAxisError((prev) => omitKeys(prev, new Set([axis.key])));
    const sigAtRequest = inspectionSig;
    void (async () => {
      try {
        const options = await onLoadAxisOptions(axis, value);
        // 조회 중 점검이 바뀌었으면 폐기 — 그 결과는 다른 점검의 선택지다
        // (위 초기화 effect가 이미 이 축의 행/캐시를 비웠다).
        if (sigAtRequest !== inspectionSigRef.current) return;
        setAxisOptions((prev) => ({ ...prev, [axis.key]: options }));
      } catch {
        if (sigAtRequest !== inspectionSigRef.current) return;
        setAxisError((prev) => ({
          ...prev,
          [axis.key]: `${axis.label} 선택지를 불러오지 못했습니다.`,
        }));
      } finally {
        if (sigAtRequest === inspectionSigRef.current) {
          setAxisLoading((prev) => omitKeys(prev, new Set([axis.key])));
        }
      }
    })();
  };
  // 모사 축 추가(섹션 경로) — 행을 세우고 `plan` 축이면 그 조작에서 조회한다(§④).
  const handleAddAxis = (axis: PreviewSimulationAxis) => {
    setAddOpen(false);
    setAddedAxisKeys((keys) => (keys.includes(axis.key) ? keys : [...keys, axis.key]));
    loadPlanAxisOptions(axis);
  };
  // 축 값 선택 — 옵션에 실려 온 값을 **그대로** 싣는다(§③ opaque: 해석하지 않는다).
  const handleSelectAxisValue = (
    axis: PreviewSimulationAxis,
    option: PreviewSimulationOption | undefined,
  ) => {
    onChange({
      ...value,
      previewSimulation: setSimulationAxisValue(simulation, axis.key, option?.value),
      // 구 전용 필드는 새 축이 이기지만(서버 폴백 순서), 남겨 두면 해제했을 때 되살아난다.
      ...(axis.payloadKind === 'opaque' ? { splitPreview: undefined } : {}),
    });
    setAxisLabels((prev) =>
      option
        ? { ...prev, [axis.key]: option.label }
        : omitKeys(prev, new Set([axis.key])),
    );
  };
  // 자유 입력 축(text/number/date) — 파싱 규칙은 프리셋 편집기와 공유(parseSimulationAxisInput).
  // 빈 문자열은 "값 없음", date는 시간 포함 UTC 변환(CLAUDE.md 시간대 정책 — 헬퍼 JSDoc이 정본).
  const handleTypeAxisValue = (axis: PreviewSimulationAxis, raw: string) => {
    const parsed = parseSimulationAxisInput(axis, raw);
    if (parsed.kind === 'incomplete') return; // 아직 값이 아니다 — 입력 도중
    const next = parsed.kind === 'unset' ? undefined : parsed.value;
    onChange({
      ...value,
      previewSimulation: setSimulationAxisValue(simulation, axis.key, next),
    });
    setAxisLabels((prev) =>
      next === undefined
        ? omitKeys(prev, new Set([axis.key]))
        : { ...prev, [axis.key]: raw },
    );
  };
  // (모사 프리셋의 적용·「현재 값을 프리셋으로 저장」 UI는 2026-09-04 성격 분리로 이 컴포넌트에서
  //  제거 — 저작·적용 모두 전역 속성 패널 「모사 데이터」 구획(SimulationPresetsSection) 몫이다.)

  // 행 제거 — 값도 함께 지운다(행만 없애면 모사가 보이지 않는 채로 계속 걸린다, §⑥).
  const handleRemoveAxis = (axis: PreviewSimulationAxis) => {
    setAddedAxisKeys((keys) => keys.filter((k) => k !== axis.key));
    setAxisLabels((prev) => omitKeys(prev, new Set([axis.key])));
    onChange({
      ...value,
      previewSimulation: setSimulationAxisValue(simulation, axis.key, undefined),
      ...(axis.payloadKind === 'opaque' ? { splitPreview: undefined } : {}),
    });
  };
  const handleUser = (v: string | undefined) => {
    onChange({ ...value, userId: v });
  };

  // 레이아웃 클래스 — vertical(모달)이면 세로 스택(라벨 위 + w-full 콤보), 아니면 기존 인라인.
  const rowCls = vertical ? 'flex flex-col gap-3' : 'flex flex-wrap items-center gap-x-3 gap-y-1';
  const fieldCls = vertical
    ? 'flex flex-col gap-1 w-full'
    : 'flex items-center gap-1 flex-1 basis-[145px] min-w-[125px] max-w-[200px]';
  const inspFieldCls = vertical
    ? 'flex flex-col gap-1 w-full'
    : 'flex items-center gap-1 flex-1 basis-[160px] min-w-[140px] max-w-[215px]';
  const labelCls = vertical
    ? 'text-xs font-semibold text-gray-600'
    : 'text-[10px] font-semibold text-gray-500 shrink-0';

  return (
    <div className="flex flex-col gap-1">
      {/* 컴팩트 인라인 레이아웃(vertical=false) — 라벨을 콤보 왼쪽에 붙이고(세로 절반) 각 항목을
          유연 폭(basis+min/max) flex로: 넓으면 4개 한 줄, 좁으면 컴팩트하게 wrap. vertical=true면
          모달용 세로 스택(라벨 위 + w-full 콤보). */}
      <div className={rowCls}>
        <label className={fieldCls}>
          <span className={labelCls}>조직</span>
          {/* eslint-disable-next-line local/catalog-picker-required -- 미리보기 컨텍스트 조직 선택(회사 목록) — 카탈로그 엔트리 아님 */}
          <CatalogCombobox
            value={companyId}
            options={companies}
            onChange={handleCompany}
            placeholder="조직 선택"
            allowClear
            disabled={disabled}
          />
        </label>
        <label className={fieldCls}>
          <span className={labelCls}>현장</span>
          {/* eslint-disable-next-line local/catalog-picker-required -- 미리보기 컨텍스트 현장 선택(현장 목록) — 카탈로그 엔트리 아님 */}
          <CatalogCombobox
            value={siteId}
            options={sites}
            onChange={handleSite}
            placeholder={companyId ? '현장 선택' : '조직 먼저'}
            allowClear
            disabled={disabled || !companyId}
          />
        </label>
        <div className={inspFieldCls}>
          <span className={labelCls}>점검(회차)</span>
          {(() => {
            // 비활성 사유별 라벨: 조직 미선택 → "조직 먼저", 점검 없음 → "점검 없음".
            const triggerDisabled = disabled || !companyId || inspections.length === 0;
            const triggerLabel = !companyId
              ? '조직 먼저'
              : inspections.length === 0
                ? '점검 없음'
                : selectedInspectionIds.length === 0
                  ? '점검 선택'
                  : selectedInspectionIds.length === 1
                    ? (inspections.find((i) => i.value === selectedInspectionIds[0])?.label ?? '1건 선택')
                    : `${selectedInspectionIds.length}건 선택`;
            const hasSelection = companyId && inspections.length > 0 && selectedInspectionIds.length > 0;
            return (
              <div ref={inspectionRef} className="relative flex-1 min-w-0">
                <button
                  type="button"
                  disabled={triggerDisabled}
                  onClick={() => setInspectionOpen((o) => !o)}
                  className={`w-full text-xs border border-gray-300 rounded px-1.5 py-1 text-left flex items-center justify-between gap-1 focus:outline-none focus:ring-1 focus:ring-brand-400 ${
                    triggerDisabled ? 'bg-gray-100 text-gray-400' : ''
                  }`}
                  // 열리는 것은 listbox가 아니라 **라디오(체크박스) 묶음**이라 haspopup을 쓰지
                  // 않는다. 보조기술에는 "펼침 상태 + 무엇을 펼치는가"만 알리면 충분하고, 그게
                  // 실제 DOM(role=radiogroup + role=radio)과도 어긋나지 않는다.
                  aria-expanded={inspectionOpen}
                  aria-controls={inspectionOpen ? inspectionPopupId : undefined}
                >
                  <span className={`truncate ${hasSelection ? '' : 'text-gray-400'}`}>{triggerLabel}</span>
                  <span className="text-gray-400 text-[10px]">▾</span>
                </button>
                {inspectionOpen && companyId && inspections.length > 0 && (
                  <div
                    id={inspectionPopupId}
                    // ⚠️ 한때 `role="listbox"`였는데 자식은 option이 아니라 **native radio를 품은
                    // label**이었다 — listbox는 자식이 option이어야 하므로 보조기술에 "선택지 0개인
                    // 목록"으로 읽힌다. 컨트롤을 바꾸는 대신(라디오는 키보드 이동·선택이 공짜다)
                    // 컨테이너 role을 실물에 맞춘다. 멀티로 되돌리면 체크박스 묶음이라 group.
                    role={ALLOW_MULTI_INSPECTION ? 'group' : 'radiogroup'}
                    aria-label="점검(회차) 선택"
                    className="absolute z-30 mt-0.5 w-full max-h-60 overflow-auto rounded border border-gray-300 bg-white shadow-lg px-1 py-0.5"
                  >
                    {inspections.map((insp) => {
                      const checked = selectedInspectionIds.includes(insp.value);
                      return (
                        <label
                          key={insp.value}
                          className={`flex items-center gap-1 text-xs px-1 py-0.5 rounded cursor-pointer ${
                            checked ? 'bg-brand-50' : 'hover:bg-gray-50'
                          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <input
                            type={ALLOW_MULTI_INSPECTION ? 'checkbox' : 'radio'}
                            name={ALLOW_MULTI_INSPECTION ? undefined : 'composer-preview-inspection'}
                            checked={checked}
                            disabled={disabled}
                            onChange={() =>
                              ALLOW_MULTI_INSPECTION
                                ? handleToggleInspection(insp.value)
                                : handleSelectInspection(insp.value)
                            }
                            className="h-3 w-3 shrink-0"
                          />
                          <span className="truncate">{insp.label}</span>
                          {insp.key && (
                            <span className="ml-auto shrink-0 text-[10px] text-gray-400 font-mono">{insp.key}</span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
        {showReportPicker && (
          <label className={fieldCls}>
            <span className={labelCls}>보고서(선택)</span>
            {(() => {
              // 재오픈 직후 목록 fetch 완료 전 — 값·보관 라벨로 한 줄을 합성해 이름을 잃지 않는다
              // (SimulationAxisRow의 plan 축 합성과 같은 문제).
              const options =
                linkedReportId && !reports.some((r) => r.value === linkedReportId)
                  ? [{ value: linkedReportId, label: reportLabel ?? '(연결한 보고서)' }, ...reports]
                  : reports;
              return (
                /* eslint-disable-next-line local/catalog-picker-required -- 미리보기 연결 보고서 선택(보고서 목록) — 카탈로그 엔트리 아님 */
                <CatalogCombobox
                  value={linkedReportId}
                  options={options}
                  onChange={handleLinkedReport}
                  placeholder={
                    !inspectionId
                      ? '점검 먼저'
                      : reports.length === 0
                        ? '연결 보고서 없음'
                        : '보고서 선택'
                  }
                  allowClear
                  clearLabel="(연결 안 함 — 현행 컨텍스트)"
                  disabled={disabled || !inspectionId || (reports.length === 0 && !linkedReportId)}
                />
              );
            })()}
            <span className="text-[10px] text-gray-400">
              선택하면 그 보고서의 저장 데이터로 미리보기합니다(값 모사는 반영되지 않고, 분할
              그룹은 함께 적용됩니다).
            </span>
          </label>
        )}
        {/* plan 축(분할 그룹) — 캐스케이드의 한 단계(결정 2026-09-04 §1 개정 절, planAxisAsField
            호스트만). 점검 하위·보고서(선택) 아래 축이다(§3 개정 순서 — 위=데이터 원천, 아래=그
            형상 조정): 선택지는 그 점검의 분할 계획에서 오고, 조회는 부수효과가 있어(§④)
            드롭다운을 **여는 조작에서 1회**만 한다(섹션의 "축을 추가하는 조작"과 같은 게이트 —
            loadPlanAxisOptions의 캐시·inspectionSig 폐기 규약을 그대로 지난다).
            ⚠️ 보고서 연결과 **조합 가능**(§3 개정) — 값 모사와 달리 배타로 잠그지 않는다. 본문
            행은 연결 중에도 컨텍스트 resolve라 이 축이 실물(그룹으로 잘린 행)과 맞추는 수단이다. */}
        {cascadePlanAxes.map((axis) => {
          const current = simulation?.[axis.key];
          const storedLabel = axisLabels[axis.key];
          // 재오픈 직후 조회 전 — 값·보관 라벨로 한 줄을 합성해 이름을 잃지 않는다
          // (SimulationAxisRow JSDoc과 같은 문제: 재조회는 부수효과라 금지).
          const planOptions =
            axisOptions[axis.key] ??
            (current !== undefined && storedLabel !== undefined
              ? [{ label: storedLabel, value: current }]
              : []);
          const selectedIdx = resolveSelectIndex(planOptions, current, storedLabel);
          return (
            <label key={axis.key} className={fieldCls}>
              <span className={labelCls}>{axis.label}</span>
              {/* eslint-disable-next-line local/catalog-picker-required -- 미리보기 분할 그룹 선택(plan 축 선택지) — 카탈로그 엔트리 아님 */}
              <CatalogCombobox
                // 값은 **인덱스**로 오간다 — opaque 값은 문자열로 표현할 수 없고, 표현하려 드는
                // 순간 이 컴포넌트가 그 형상을 알게 된다(§③). 값 일치 우선 → 라벨 폴백은
                // resolveSelectIndex가 진다.
                value={selectedIdx >= 0 ? String(selectedIdx) : undefined}
                options={planOptions.map((opt, idx) => ({
                  value: String(idx),
                  label: opt.label,
                }))}
                onChange={(v) =>
                  handleSelectAxisValue(
                    axis,
                    v !== undefined ? planOptions[Number(v)] : undefined,
                  )
                }
                placeholder={!inspectionId ? '점검 먼저' : axis.unsetLabel}
                allowClear
                clearLabel={`(${axis.unsetLabel})`}
                disabled={disabled || !inspectionId}
                onOpen={() => loadPlanAxisOptions(axis)}
                emptyText={
                  axisLoading[axis.key] ? '불러오는 중…' : '고를 수 있는 값이 없습니다.'
                }
              />
              {axisError[axis.key] && (
                <span className="text-[11px] text-red-600">{axisError[axis.key]}</span>
              )}
              {current === undefined && !!inspectionId && (
                <span className="text-[10px] text-gray-400">
                  값을 고르지 않으면 {axis.unsetLabel}입니다.
                </span>
              )}
            </label>
          );
        })}
        <label className={fieldCls}>
          <span className={labelCls}>계정</span>
          {/* eslint-disable-next-line local/catalog-picker-required -- 미리보기 컨텍스트 계정 선택(사용자 목록) — 카탈로그 엔트리 아님 */}
          <CatalogCombobox
            value={userId}
            options={users}
            onChange={handleUser}
            placeholder={companyId ? '계정 선택' : '조직 먼저'}
            allowClear
            disabled={disabled || !companyId}
          />
        </label>
      </div>
      {loadErr && <span className="text-[11px] text-red-600">{loadErr}</span>}
      {/* 데이터 모사 — **추가형 섹션**(결정 2026-09-01 §③-2). 위 엔티티 캐스케이드와 시각적으로
          갈라 둔다: 앞은 *실데이터를 고르는 것*이고 여기는 *값을 박는 것*이라 성질이 다르다.
          빈 상태로 시작하고 저작자가 고른 축만 행으로 쌓인다 — 쌓인 행이 곧 "지금 모사 중인 것
          전부"라 §⑥(모사 중임이 계속 보인다)이 목록 자체로 성립하고, 해제 수단(✕)이 같은 자리에 있다.
          ⚠️ 이 컴포넌트는 축이 무엇을 뜻하는지 모른다 — label/group/widget/options만 읽는다.
          planAxisAsField 호스트(실데이터 반영 모달)에서는 plan 축이 위 캐스케이드 칸으로 승격돼
          섹션에서 빠진다 — 모달처럼 plan만 넘기는 호스트라면 섹션이 통째로 사라진다. */}
      {sectionAxes.length > 0 && (
        <div className="mt-3 flex flex-col gap-1.5 border-t border-gray-200 pt-2">
          <div className="flex items-baseline gap-2">
            <span className={labelCls}>데이터 모사</span>
            {/* 규약 ⑤를 이름이 말하게 — 배포 후 사용자는 실데이터로 동작한다. */}
            <span className="text-[10px] text-gray-400">저작 중에만 적용됩니다</span>
          </div>
          {/* 배타(결정 2026-09-04 §3) — 보고서 연결 중이면 서버가 모사를 무시하므로, 조작도
              막고 이유를 말한다(막기만 하면 "고쳤는데 안 바뀐다"로 보인다). */}
          {!!linkedReportId && (
            <span className="text-[11px] text-amber-600">
              실제 보고서 연결 중 — 모사가 반영되지 않습니다.
            </span>
          )}
          {activeAxes.map((axis) => (
            // 조회된 선택지 — `plan` 축은 **모달을 다시 열면 비어 있다**(재조회는 부수효과가 있어
            // "축을 추가하는 조작에서 1회"만 한다, EditorPage `loadAxisOptions` 주석). 그때도 값과
            // 라벨은 남아 있으므로 행 편집기가 그 둘로 선택지 한 줄을 합성한다(SimulationAxisRow
            // JSDoc — 2026-09-02 dev 실측의 "화면만 거짓말" 방지).
            <SimulationAxisRow
              key={axis.key}
              axis={axis}
              current={simulation?.[axis.key]}
              options={axis.options ?? axisOptions[axis.key]}
              storedLabel={axisLabels[axis.key]}
              loading={!!axisLoading[axis.key]}
              error={axisError[axis.key]}
              disabled={disabled || !!linkedReportId}
              radioNamePrefix="composer-preview-sim"
              onSelectOption={(opt) => handleSelectAxisValue(axis, opt)}
              onTypeValue={(raw) => handleTypeAxisValue(axis, raw)}
              onRemove={() => handleRemoveAxis(axis)}
            />
          ))}
          {candidateAxes.length > 0 && (
            <div ref={addRef} className="relative">
              <button
                type="button"
                disabled={disabled || !!linkedReportId}
                onClick={() => setAddOpen((o) => !o)}
                className="rounded border border-dashed border-gray-300 px-2 py-1 text-xs text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                aria-haspopup="listbox"
                aria-expanded={addOpen}
              >
                ＋ 모사 값 추가
              </button>
              {addOpen && (
                <div
                  role="listbox"
                  className="absolute z-30 mt-0.5 max-h-60 w-72 overflow-auto rounded border border-gray-300 bg-white px-1 py-0.5 shadow-lg"
                >
                  {/* 그룹 묶음은 **선언(axis.group)이 정한다** — 여기서 다시 판정하면 도메인
                      지식이 렌더로 샌다(규약 ①). 그룹이 비면 헤더도 안 뜬다. */}
                  {CANDIDATE_GROUPS.map(({ group, title, hint }) => {
                    const inGroup = candidateAxes.filter((a) => a.group === group);
                    if (inGroup.length === 0) return null;
                    return (
                      <div key={group}>
                        <div className="px-1 pt-1 pb-0.5 text-[10px] font-semibold text-gray-500">
                          {title}
                          <span className="ml-1 font-normal text-gray-400">{hint}</span>
                        </div>
                        {inGroup.map((axis) => (
                          // 설명 툴팁(2026-09-03 사용자 요청) — 내용은 파생이 채운
                          // axis.description뿐이다(규약 ① — 여기서 도메인 문장을 만들지 않는다).
                          // 포털 Tooltip이라 listbox의 overflow-auto에 잘리지 않는다.
                          <Tooltip key={axis.key} content={axis.description}>
                            <button
                              type="button"
                              role="option"
                              aria-selected={false}
                              onClick={() => handleAddAxis(axis)}
                              className="flex w-full items-center gap-1 rounded px-1 py-0.5 text-left text-xs hover:bg-gray-50"
                            >
                              <span className="truncate">{axis.label}</span>
                              <span className="ml-auto shrink-0 font-mono text-[10px] text-gray-400">
                                {axis.key}
                              </span>
                            </button>
                          </Tooltip>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
